import { LanguageIcon } from '@/utils/helper';
import { getAllLocales, getLocale, setLocale, useIntl } from '@umijs/max';
import { Button, Dropdown, MenuProps, Tooltip } from 'antd';
import React, { useState } from 'react';
import { useMediaQuery } from 'react-responsive';
import { Colors } from '../../../config/colors';

// Menu-panel language switcher. Mounted inside the sidebar's SidebarActions
// (src/components/RightContent/RightActions.tsx), immediately above the
// Logout action, so it renders as a descendant of the same provider tree
// (IntlProvider, model providers, etc.) as the rest of the authenticated
// app shell instead of as a global fixture wired up in app.tsx.
//
// Styled to match its sibling menu-panel actions (Contact Us, Logout) - a
// plain icon+text button, using the same `logoutButton` class/color/Tooltip
// pattern as OrganisationSwitcher/AngelTeamsButton/LogoutButton - rather than
// a standalone floating widget. Clicking it opens a small dropdown menu of
// the 9 supported locales.
const LANG_CONFIG: Record<string, { label: string; flag: string }> = {
  'en-US': { label: 'English', flag: '🇺🇸' },
  'zh-CN': { label: '简体中文', flag: '🇨🇳' },
  'zh-TW': { label: '繁體中文', flag: '🇹🇼' },
  'id-ID': { label: 'Bahasa Indonesia', flag: '🇮🇩' },
  'th-TH': { label: 'ไทย', flag: '🇹🇭' },
  'ko-KR': { label: '한국어', flag: '🇰🇷' },
  'ms-MY': { label: 'Bahasa Melayu', flag: '🇲🇾' },
  'ja-JP': { label: '日本語', flag: '🇯🇵' },
  'hi-IN': { label: 'हिन्दी', flag: '🇮🇳' },
};

export type LanguageSwitcherProps = {
  collapsed?: boolean;
  actionClassName?: string;
  iconColor?: string;
  iconSize?: number;
  labelOverride?: string;
};

type TriggerButtonProps = {
  actionClassName?: string;
  iconColor?: string;
  iconSize?: number;
  showLabel: boolean;
  label: string;
};

// Extracted to module scope (rather than defined inline in LanguageSwitcher)
// so it isn't recreated - and remounted - on every render (typescript:S6478).
const TriggerButton = ({
  actionClassName,
  iconColor,
  iconSize,
  showLabel,
  label,
}: TriggerButtonProps) => (
  <Button
    type="text"
    style={{ color: iconColor ?? Colors.logoutColor }}
    className={`logoutButton ${actionClassName ?? ''}`}
    icon={
      <span
        style={{
          display: 'inline-flex',
          transform: showLabel ? 'translateY(-3px)' : undefined,
        }}
      >
        <LanguageIcon size={iconSize} color={iconColor ?? Colors.logoutColor} />
      </span>
    }
    aria-label={label}
  >
    {showLabel && <span style={{ fontSize: 14 }}>{label}</span>}
  </Button>
);

const LanguageSwitcher: React.FC<LanguageSwitcherProps> = ({
  actionClassName,
  collapsed,
  iconColor,
  iconSize,
  labelOverride,
}) => {
  const intl = useIntl();
  const showToopTip = useMediaQuery({ minWidth: 768, maxWidth: 991 });
  const [currentLocale, setCurrentLocale] = useState(() => getLocale());

  const locales = getAllLocales().filter((l) => LANG_CONFIG[l]);

  const handleSelect = (locale: string) => {
    if (locale === currentLocale) return;
    setCurrentLocale(locale);
    // Umi's setLocale persists the choice (localStorage) and reloads by
    // default so antd/date-picker/etc. locale-dependent bits re-init too.
    setLocale(locale);
  };

  const items: MenuProps['items'] = locales.map((locale) => {
    const cfg = LANG_CONFIG[locale] || { label: locale, flag: '🌐' };
    const active = locale === currentLocale;
    return {
      key: locale,
      label: (
        <span
          style={{ display: 'flex', alignItems: 'center', gap: 8, fontWeight: active ? 600 : 400 }}
        >
          {/* Flag glyph is decorative text, not an image - aria-hidden here and
          let the visible label text (below) provide the accessible name
          instead of a role="img" span (typescript:S6819). */}
          <span aria-hidden="true">{cfg.flag}</span>
          {cfg.label}
        </span>
      ),
      onClick: () => handleSelect(locale),
    };
  });

  const label =
    labelOverride ??
    intl.formatMessage({
      id: 'components.languageSwitcher.switchLanguageTooltip',
      defaultMessage: 'Switch language',
    });

  const showLabel = !collapsed && !showToopTip;

  return (
    <Dropdown menu={{ items }} trigger={['click']} placement="topLeft">
      {showToopTip || collapsed ? (
        <Tooltip title={label} placement="right">
          <span>
            <TriggerButton
              actionClassName={actionClassName}
              iconColor={iconColor}
              iconSize={iconSize}
              showLabel={showLabel}
              label={label}
            />
          </span>
        </Tooltip>
      ) : (
        <span>
          <TriggerButton
            actionClassName={actionClassName}
            iconColor={iconColor}
            iconSize={iconSize}
            showLabel={showLabel}
            label={label}
          />
        </span>
      )}
    </Dropdown>
  );
};

export default LanguageSwitcher;
